// features/partners/page/partners-list/partners-list.ts
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  ViewChildren,
  QueryList,
} from '@angular/core';
import { Router } from '@angular/router';
import { MatTabsModule } from '@angular/material/tabs';
import { Sort } from '@angular/material/sort';

import { MatDialog, MatDialogModule } from '@angular/material/dialog';

import { UiMoleculeMatTableComponent } from '@shared/components/molecules/ui-molecule-mat-table/ui-molecule-mat-table.component';
import { TableColumn } from '@shared/components/molecules/ui-molecule-mat-table/interfaces/table-column';
import { TableAction } from '@shared/components/molecules/ui-molecule-mat-table/interfaces/table-action';
import { ActionEvent } from '@shared/components/molecules/ui-molecule-mat-table/interfaces/action-event';

import { UiAtomButtonComponent } from '@shared/components/atoms/ui-atom-button/ui-atom-button.component';
import { UiAtomGlobalSearchComponent } from '@shared/components/atoms/ui-atom-global-search/ui-atom-global-search.component';
import { UiMoleculeDialogDeleteWithConfirmComponent } from '@shared/components/molecules/ui-molecule-dialog-delete-with-confirm/ui-molecule-dialog-delete-with-confirm.component';
import { UiOrganismMainNavComponent } from '@shared/components/organisms/ui-organism-main-nav/ui-organism-main-nav.component';
import { UiAtomSpacerComponent } from '@shared/components/atoms/ui-atom-spacer/ui-atom-spacer.component';
import { UiAtomTextComponent } from '@shared/components/atoms/ui-atom-text/ui-atom-text.component';
import {
  FormUpsertDialogComponent,
  FormDialogResult,
} from '@shared/components/molecules/ui-molecule-dialog-form-upsert/ui-molecule-dialog-form-upsert.component';
import {
  FormResponseDialogComponent,
  FormResponseData,
} from '@shared/components/molecules/ui-molecule-dialog-form-response/ui-molecule-dialog-form-response.component';
import {
  DialogAdminUpsertService,
  AdminDialogResult,
} from '@shared/services/dialog-admin-upsert.service';
import {
  UserService,
  UserCreate,
  UserUpdate,
  UserListParams,
} from '@core/services/use-cases/user.service';
import { UiMoleculeDialogAdminUpsertComponent } from '@shared/components/molecules/ui-molecule-dialog-admin-upsert/ui-molecule-dialog-admin-upsert.component';
import {
  PartnerUpsertDialogComponent,
  PartnerDialogResult,
} from '@shared/components/molecules/ui-molecule-dialog-partner-upsert/ui-molecule-dialog-partner-upsert.component';

import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { Partner } from '@core/entities/models/partner.model';
import { PartnerService, PartnerListParams } from '@core/services/use-cases/partner.service';
import { CountryService } from '@core/services/use-cases/country.service';
import { BreadcrumbService } from '@shared/services/breadcrumb.service';
import { FormService, FormListParams } from '@core/services/use-cases/form.service';
import { DialogFormDashboardService } from '@shared/services/dialog-form-dashboard.service';
import { RoleRedirectService } from '@core/services/role-redirect.service';
import { AppRoutes } from '@core/constants/app.routes';
import { ActivatedRoute } from '@angular/router';
import { SnackbarService } from '@shared/services/snackbar.service';
import { SessionService } from '@core/services/use-cases/session.service';
import { UserRole } from '@core/entities/models/role.model';
import { formatDate } from '@core/utils/date.utils';
import {
  UiOrganismTitleLogosComponent
} from '@shared/components/organisms/ui-organism-title-logos/ui-organism-title-logos.component';

@Component({
  selector: 'app-partners-list',
  standalone: true,
  imports: [
    CommonModule,
    MatTabsModule,
    UiMoleculeMatTableComponent,
    UiAtomButtonComponent,
    MatDialogModule,
    TranslatePipe,
    UiOrganismMainNavComponent,
    UiAtomSpacerComponent,
    UiAtomTextComponent,
    UiMoleculeDialogAdminUpsertComponent,
    FormUpsertDialogComponent,
    PartnerUpsertDialogComponent,
    UiAtomGlobalSearchComponent,
    UiOrganismTitleLogosComponent,
  ],
  templateUrl: './partners-list.html',
  styleUrl: './partners-list.scss',
  changeDetection: ChangeDetectionStrategy.Default,
})
export class PartnersList implements OnInit {
  @ViewChildren(UiMoleculeMatTableComponent) tables!: QueryList<UiMoleculeMatTableComponent>;

  /** Datos que alimentan la tabla */
  protected tableData: (Partner & { beneficiariesCount?: number })[] = [];

  /** Definición de columnas */
  protected tableColumns: TableColumn[] = [];

  /** Datos de usuarios admin_partners */
  protected usersData: any[] = [];
  protected userColumns: TableColumn[] = [];

  /** Datos de forms del país */
  protected formsData: any[] = [];
  protected formsColumns: TableColumn[] = [];

  // Título dinámico según nivel jerárquico
  protected pageTitle = ''; // Nivel 2: [Nombre del Country] - se establecerá dinámicamente

  /** Acciones de la tabla */
  protected readonly tableActions: TableAction[] = [
    {
      icon: 'edit',
      tooltip: 'Edit partner',
      tooltipKey: 'partners.list.table.actions.edit',
      event: 'edit',
    },
    {
      icon: 'delete',
      tooltip: 'Delete partner',
      tooltipKey: 'partners.list.table.actions.delete',
      event: 'delete',
    },
  ];

  protected readonly userActions: TableAction[] = [
    {
      icon: 'person',
      tooltip: 'Deactivate user',
      tooltipKey: 'users.list.table.actions.deactivate',
      event: 'toggle-user-status',
      showIf: (user: any) => user.status === 'ACTIVE',
    },
    {
      icon: 'person_off',
      tooltip: 'Activate user',
      tooltipKey: 'users.list.table.actions.activate',
      event: 'toggle-user-status',
      showIf: (user: any) => user.status === 'BLOCKED',
    },
    {
      icon: 'edit',
      tooltip: 'Edit user',
      tooltipKey: 'users.list.table.actions.edit',
      event: 'edit-user',
    },
    {
      icon: 'delete',
      tooltip: 'Delete user',
      tooltipKey: 'users.list.table.actions.delete',
      event: 'delete-user',
    },
  ];

  protected readonly formsActions: TableAction[] = [
    {
      icon: 'rocket',
      tooltip: 'Publish',
      tooltipKey: 'forms.list.table.actions.publish',
      event: 'publish-form',
      showIf: (form: any) => {
        // Cohete si publication es null: DRAFT → finalize+publish, FINAL → solo publish
        return form.publication === null && this.canPublish();
      },
    },
    {
      icon: 'edit',
      tooltip: 'Edit form',
      tooltipKey: 'forms.list.table.actions.edit',
      event: 'edit-form',
      showIf: (form: any) => form.status === 'DRAFT',
    },
    {
      icon: 'delete',
      tooltip: 'Delete form',
      tooltipKey: 'forms.list.table.actions.delete',
      event: 'delete-form',
      showIf: (form: any) => form.status === 'DRAFT',
    },
    // {
    //   icon: 'link',
    //   tooltip: 'Copy link',
    //   tooltipKey: 'forms.list.table.actions.copyLink',
    //   event: 'copy-link',
    //   showIf: (form: any) => form.status === 'FINAL' && form.publication !== null,
    // },
    {
      icon: 'space_dashboard',
      tooltip: 'View dashboard',
      tooltipKey: 'forms.list.table.actions.dashboard',
      event: 'dashboard-form',
      disabledIf: (form: any) => {
        // Sin publicación → 0 respuestas. En contexto partner, la API trae
        // `submissions.total` y se deshabilita cuando es 0. En contexto
        // country no llega ese campo y se deja habilitado (el diálogo
        // mostrará empty state si nadie ha respondido).
        if (!form.publication) return true;
        const total = form.publication.submissions?.total;
        return total !== undefined && total === 0;
      },
    },
  ];

  protected pageIndex = 0;
  protected pageSize = 10;
  protected totalItems = 0;
  protected userPageIndex = 0;
  protected userPageSize = 10;
  protected userTotalItems = 0;
  protected formsPageIndex = 0;
  protected formsPageSize = 10;
  protected formsTotalItems = 0;
  protected selectedTab = 0;
  protected isLoading = false;
  protected isLoadingUsers = false;
  protected isLoadingForms = false;
  private currentCountryId: string | undefined;

  // Ordenación servidor - Partners
  protected partnerSortBy: PartnerListParams['sortBy'] = undefined;
  protected partnerSortOrder: 'ASC' | 'DESC' | undefined = undefined;
  protected partnerFilter: string | undefined = undefined;

  // Ordenación servidor - Users
  protected userSortBy: UserListParams['sortBy'] = undefined;
  protected userSortOrder: 'ASC' | 'DESC' | undefined = undefined;
  protected userFilter: string | undefined = undefined;

  // Ordenación servidor - Forms
  protected formsSortBy: FormListParams['sortBy'] = undefined;
  protected formsSortOrder: 'ASC' | 'DESC' | undefined = undefined;
  protected formsFilter: string | undefined = undefined;

  constructor(
    private readonly partnerService: PartnerService,
    private readonly countryService: CountryService,
    private readonly userService: UserService,
    private readonly formService: FormService,
    private readonly router: Router,
    private readonly dialog: MatDialog,
    private readonly cdr: ChangeDetectorRef,
    private readonly translateService: TranslateService,
    private readonly roleRedirectService: RoleRedirectService,
    private readonly route: ActivatedRoute,
    private readonly dialogAdminUpsert: DialogAdminUpsertService,
    private readonly snackbarService: SnackbarService,
    private readonly sessionService: SessionService,
    private readonly breadcrumbService: BreadcrumbService,
    private readonly dashboardDialog: DialogFormDashboardService,
  ) {}

  /**
   * Verifica si el usuario actual puede publicar (SUPERADMIN o COUNTRY_ADMIN)
   */
  private canPublish(): boolean {
    const currentUser = this.sessionService.getCurrentUser();
    return currentUser?.role === UserRole.ADMIN || currentUser?.role === UserRole.COUNTRY_ADMIN;
  }

  ngOnInit(): void {
    this.buildTableColumns();
    this.buildUserColumns();
    this.buildFormsColumns();
    // Solo cargar la primera tab inicialmente
    this.loadTable();
    this.setPageTitle();
  }

  private setPageTitle(): void {
    const countryId = this.route.snapshot.paramMap.get('countryId');
    if (countryId) {
      this.breadcrumbService.getCountryName(countryId).then((name) => {
        this.pageTitle = name;
        this.cdr.markForCheck();
      });
    } else {
      this.pageTitle = 'Partners';
      this.cdr.markForCheck();
    }
  }

  private buildTableColumns(): void {
    this.tableColumns = [
      {
        key: 'name',
        title: 'Name',
        titleKey: 'partners.list.table.columns.name',
        type: 'text',
        sortable: true,
        filterable: true,
        filterType: 'input',
        placeholder: 'Filter by partner name',
        placeholderKey: 'partners.list.table.filters.name',
      },
      // HIDDEN-COLUMNS: 'createdAt' y 'updatedAt' ocultas temporalmente en la pestaña Partners.
      // Para volver a mostrarlas, descomentar el bloque siguiente (no requiere más cambios:
      // las claves i18n y el mapeo de datos siguen vigentes).
      // {
      //   key: 'createdAt',
      //   title: 'CreatedAt',
      //   titleKey: 'partners.list.table.columns.createdAt',
      //   type: 'text',
      //   width: '120px',
      //   sortable: true,
      //   filterable: false,
      // },
      // {
      //   key: 'updatedAt',
      //   title: 'UpdatedAt',
      //   titleKey: 'partners.list.table.columns.updatedAt',
      //   type: 'text',
      //   width: '120px',
      //   sortable: true,
      //   filterable: false,
      // },
      {
        key: 'beneficiariesCount',
        title: 'Beneficiaries',
        titleKey: 'partners.list.table.columns.beneficiaries',
        type: 'text',
        width: '120px',
        sortable: true,
        filterable: false,
      },
    ];
  }

  private loadTable(searchTerm?: string): void {
    this.isLoading = true;
    // Obtener countryId de la ruta actual
    this.route.paramMap.subscribe((params) => {
      const countryId = params.get('countryId');
      const requestParams: PartnerListParams = {
        offset: this.pageIndex * this.pageSize,
        limit: this.pageSize,
      };

      if (searchTerm && searchTerm.length >= 3) {
        requestParams.filter = searchTerm;
      }

      // Agregar countryId para filtrar partners del country actual
      if (countryId) {
        requestParams.countryId = countryId;
      }

      // Ordenación servidor
      if (this.partnerSortBy) {
        requestParams.sortBy = this.partnerSortBy;
      }
      if (this.partnerSortOrder) {
        requestParams.sortOrder = this.partnerSortOrder;
      }

      this.partnerService.getAll(requestParams).subscribe({
        next: (response) => {
          this.tableData = response.items.map((p) => ({
            ...p,
            createdAt: formatDate(p.createdAt),
            updatedAt: formatDate(p.updatedAt),
            beneficiariesCount: p.counts?.beneficiaries || 0,
          }));
          this.totalItems = response.total;
          this.isLoading = false;
          this.cdr.markForCheck();
        },
        error: (err) => {
          this.snackbarService.notifyHttpError(err);
          this.isLoading = false;
        },
      });
    });
  }

  protected onTableAction(actionEvent: ActionEvent): void {
    const { event, rowData } = actionEvent;
    const row = rowData as Partner;

    switch (event) {
      case 'edit':
        this.onEdit(row);
        break;
      case 'delete':
        this.onDelete(row);
        break;
      default:
        console.warn('[PartnersList] Acción no reconocida:', event);
        break;
    }
  }

  protected onTableRowClick(row: Partner): void {
    // Navegación jerárquica: al hacer click en un partner, ir a sus beneficiarios
    // Obtener el countryId desde la ruta actual
    this.route.paramMap.subscribe((params) => {
      const countryId = params.get('countryId');
      const navigationUrl = this.roleRedirectService.buildNavigationUrl(
        'partner',
        row.id,
        countryId || undefined,
      );
      void this.router.navigate(navigationUrl).catch(() => {});
    });
  }

  protected onTablePageChange(event: { pageIndex: number; pageSize: number }): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadTable();
  }

  protected onTableSortChange(sort: Sort): void {
    const columnToApiField: Record<string, PartnerListParams['sortBy']> = {
      name: 'name',
      createdAt: 'createdAt',
      updatedAt: 'updatedAt',
      beneficiariesCount: 'counts.beneficiaries',
    };

    if (sort.direction) {
      this.partnerSortBy = columnToApiField[sort.active];
      this.partnerSortOrder = sort.direction.toUpperCase() as 'ASC' | 'DESC';
    } else {
      this.partnerSortBy = undefined;
      this.partnerSortOrder = undefined;
    }

    this.pageIndex = 0;
    this.loadTable();
  }

  private onEdit(row: Partner): void {
    // Obtener el countryId desde la ruta actual
    this.route.paramMap.subscribe((params) => {
      const countryId = params.get('countryId');

      const dialogRef = this.dialog.open(PartnerUpsertDialogComponent, {
        width: '600px',
        maxHeight: '90vh',
        panelClass: ['dialog-md', 'dialog-safe-area'],
        data: {
          title: this.translateService.instant('partners.form.editTitle'),
          mode: 'edit',
          partner: row,
          context: countryId ? 'country' : 'global',
          countryId: countryId || undefined,
        },
      });

      dialogRef.afterClosed().subscribe((result: PartnerDialogResult | undefined) => {
        if (result && result.mode === 'edit' && result.partnerId) {
          this.handlePartnerUpdate(result.partnerId, result.payload);
        }
      });
    });
  }

  private onDelete(row: Partner): void {
    const dialogRef = this.dialog.open(UiMoleculeDialogDeleteWithConfirmComponent, {
      disableClose: true,
      autoFocus: false,
      panelClass: ['dialog-sm', 'dialog-safe-area'],
      data: {
        itemName: row.name,
      },
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (!confirmed) return;
      this.partnerService.delete(row.id).subscribe({
        next: () => this.loadTable(),
        error: (err) => this.snackbarService.notifyHttpError(err),
      });
    });
  }

  protected openCreatePartnerDialog(): void {
    // Obtener countryId de la ruta actual
    const countryId = this.route.snapshot.paramMap.get('countryId');

    const dialogRef = this.dialog.open(PartnerUpsertDialogComponent, {
      width: '600px',
      maxHeight: '90vh',
      panelClass: ['dialog-md', 'dialog-safe-area'],
      data: {
        title: this.translateService.instant('partners.form.createTitle'),
        mode: 'create',
        context: 'country',
        countryId: countryId || undefined,
      },
    });

    dialogRef.afterClosed().subscribe((result: PartnerDialogResult | undefined) => {
      if (result && result.mode === 'create') {
        this.handlePartnerCreate(result.payload, result.context, result.countryId);
      }
    });
  }

  private buildUserColumns(): void {
    this.userColumns = [
      {
        key: 'email',
        title: 'Email',
        titleKey: 'users.list.table.columns.email',
        type: 'email',
        width: '220px',
        sortable: true,
        filterable: false,
      },
      {
        key: 'firstName',
        title: 'FirstName',
        titleKey: 'users.list.table.columns.firstName',
        type: 'text',
        sortable: true,
        filterable: false,
      },
      {
        key: 'lastName',
        title: 'LastName',
        titleKey: 'users.list.table.columns.lastName',
        type: 'text',
        sortable: true,
        filterable: false,
      },
      {
        key: 'enabled',
        title: 'Enabled',
        titleKey: 'users.list.table.columns.enabled',
        type: 'text',
        width: '90px',
        filterable: false,
      },
      // HIDDEN-COLUMNS: 'createdAt' y 'updatedAt' ocultas temporalmente en la pestaña Users.
      // Para volver a mostrarlas, descomentar el bloque siguiente (no requiere más cambios:
      // las claves i18n y el mapeo de datos siguen vigentes).
      // {
      //   key: 'createdAt',
      //   title: 'CreatedAt',
      //   titleKey: 'users.list.table.columns.createdAt',
      //   type: 'text',
      //   width: '120px',
      //   sortable: true,
      //   filterable: false,
      // },
      // {
      //   key: 'updatedAt',
      //   title: 'UpdatedAt',
      //   titleKey: 'users.list.table.columns.updatedAt',
      //   type: 'text',
      //   width: '120px',
      //   sortable: true,
      //   filterable: false,
      // },
    ];
  }

  private loadUsers(): void {
    this.isLoadingUsers = true;
    this.route.paramMap.subscribe((params) => {
      const countryId = params.get('countryId');

      if (countryId) {
        const userParams: UserListParams = {
          offset: this.userPageIndex * this.userPageSize,
          limit: this.userPageSize,
          countryId: countryId,
          role: 'COUNTRY_ADMIN',
        };

        if (this.userFilter && this.userFilter.length >= 3) {
          userParams.filter = this.userFilter;
        }
        if (this.userSortBy) {
          userParams.sortBy = this.userSortBy;
        }
        if (this.userSortOrder) {
          userParams.sortOrder = this.userSortOrder;
        }

        this.userService.getAll(userParams).subscribe({
          next: (users) => {
            // Mapear usuarios al formato esperado por la tabla
            this.usersData = users.items.map((user) => ({
              id: user.id,
              email: user.email,
              firstName: user.firstName,
              lastName: user.lastName,
              enabled: user.status === 'ACTIVE' ? '✓' : '',
              status: user.status, // ✅ CONSERVAR la propiedad original
              createdAt: formatDate(user.createdAt),
              updatedAt: formatDate(user.updatedAt),
            }));
            this.userTotalItems = users.total;
            this.isLoadingUsers = false;
            this.cdr.markForCheck();
          },
          error: (error) => {
            this.snackbarService.notifyHttpError(error);
            this.usersData = [];
            this.isLoadingUsers = false;
            this.cdr.markForCheck();
          },
        });
      } else {
        this.usersData = [];
        this.isLoadingUsers = false;
        this.cdr.markForCheck();
      }
    });
  }

  protected openCreateUserDialog(): void {
    // Obtener el countryId desde la ruta actual
    this.route.paramMap.subscribe((params) => {
      const countryId = params.get('countryId');

      if (countryId) {
        // Abrir diálogo para crear Country Admin (nivel country)
        this.dialogAdminUpsert
          .open({
            title: 'Create Country Admin',
            message:
              'Create a new country administrator with access to all partners within this country.',
            availableRoles: ['COUNTRY_ADMIN'],
            context: 'country',
            countryId: countryId,
            mode: 'create',
          })
          .subscribe((result: AdminDialogResult | undefined) => {
            if (result && result.mode === 'create') {
              this.userService.create(result.payload as UserCreate).subscribe({
                next: () => {
                  // Recargar la lista de usuarios
                  this.loadUsers();
                },
                error: (error) => this.snackbarService.notifyHttpError(error),
              });
            }
          });
      }
    });
  }

  protected onUserAction(actionEvent: ActionEvent): void {
    const { event, rowData } = actionEvent;

    switch (event) {
      case 'edit-user':
        this.openEditUserDialog(rowData);
        break;
      case 'toggle-user-status':
        this.toggleUserStatus(rowData);
        break;
      case 'delete-user':
        this.openDeleteUserDialog(rowData);
        break;
      default:
        break;
    }
  }

  private openEditUserDialog(user: any): void {
    this.route.paramMap.subscribe((params) => {
      const countryId = params.get('countryId');

      if (countryId) {
        this.dialogAdminUpsert
          .open({
            title: 'Edit Country Admin',
            message: 'Edit the country administrator details.',
            defaults: {
              firstName: user.firstName || user.name?.split(' ')[0] || '',
              lastName: user.lastName || user.name?.split(' ').slice(1).join(' ') || '',
              email: user.email,
              role: user.role,
            },
            availableRoles: ['COUNTRY_ADMIN'],
            context: 'country',
            mode: 'edit',
            user: user,
          })
          .subscribe((result: AdminDialogResult | undefined) => {
            if (result && result.mode === 'edit' && result.userId) {
              this.handleUserUpdate(
                result.userId,
                result.payload as UserUpdate,
                result.newPassword,
              );
            }
          });
      }
    });
  }

  private openDeleteUserDialog(user: any): void {
    const dialogRef = this.dialog.open(UiMoleculeDialogDeleteWithConfirmComponent, {
      disableClose: true,
      autoFocus: false,
      panelClass: ['dialog-sm', 'dialog-safe-area'],
      data: { itemName: user.name },
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (!confirmed) return;
      this.userService.delete(user.id).subscribe({
        next: () => this.loadUsers(),
        error: (err) => this.snackbarService.notifyHttpError(err),
      });
    });
  }

  private toggleUserStatus(user: any): void {
    const isCurrentlyActive = user.status === 'ACTIVE';
    const action = isCurrentlyActive ? 'block' : 'unblock';
    const actionText = isCurrentlyActive ? 'blocking' : 'unblocking';

    const serviceCall = isCurrentlyActive
      ? this.userService.block(user.id)
      : this.userService.unblock(user.id);

    serviceCall.subscribe({
      next: (updatedUser) => {
        this.loadUsers(); // Recargar la tabla para ver el estado actualizado
      },
      error: (err) => this.snackbarService.notifyHttpError(err),
    });
  }

  protected onUserPageChange(event: { pageIndex: number; pageSize: number }): void {
    this.userPageIndex = event.pageIndex;
    this.userPageSize = event.pageSize;
    this.loadUsers();
  }

  protected onUserSortChange(sort: Sort): void {
    const columnToApiField: Record<string, UserListParams['sortBy']> = {
      email: 'email',
      firstName: 'firstName',
      lastName: 'lastName',
      createdAt: 'createdAt',
      updatedAt: 'updatedAt',
    };

    if (sort.direction) {
      this.userSortBy = columnToApiField[sort.active];
      this.userSortOrder = sort.direction.toUpperCase() as 'ASC' | 'DESC';
    } else {
      this.userSortBy = undefined;
      this.userSortOrder = undefined;
    }

    this.userPageIndex = 0;
    this.loadUsers();
  }

  protected onUserGlobalSearch(searchTerm: string): void {
    this.userFilter = searchTerm;
    if (searchTerm && searchTerm.length >= 3) {
      this.userPageIndex = 0;
      this.loadUsers();
    } else if (!searchTerm || searchTerm.length === 0) {
      this.userFilter = undefined;
      this.loadUsers();
    }
  }

  protected onTabChange(index: number): void {
    this.selectedTab = index;

    // Cargar datos según la tab seleccionada (lazy loading)
    switch (index) {
      case 0: // Partners tab
        if (this.tableData.length === 0) {
          this.loadTable();
        }
        break;
      case 1: // Forms tab
        if (this.formsData.length === 0) {
          this.loadForms();
        }
        break;
      case 2: // Users tab
        if (this.usersData.length === 0) {
          this.loadUsers();
        }
        break;
    }
  }

  protected onGlobalSearch(searchTerm: string): void {
    if (searchTerm && searchTerm.length >= 3) {
      this.pageIndex = 0;
      this.loadTable(searchTerm);
    } else if (!searchTerm || searchTerm.length === 0) {
      this.loadTable();
    }
  }

  protected onTableFilterChange(filters: Record<string, string>): void {
    const filterValue = Object.values(filters).find((v) => v && v.length >= 3);
    if (filterValue) {
      this.pageIndex = 0;
      this.loadTable(filterValue);
    } else if (Object.values(filters).every((v) => !v || v.length === 0)) {
      this.loadTable();
    }
  }

  // Forms tab methods
  private buildFormsColumns(): void {
    this.formsColumns = [
      {
        key: 'name',
        title: 'Name',
        titleKey: 'forms.list.table.columns.name',
        type: 'text',
        sortable: true,
        filterable: true,
        filterType: 'input',
        placeholder: 'Filter by name',
        placeholderKey: 'forms.list.table.filters.name',
      },
      {
        key: 'published',
        title: 'Published',
        titleKey: 'forms.list.table.columns.published',
        type: 'text',
        width: '90px',
        filterable: false,
      },
      // HIDDEN-COLUMNS: 'createdAt' y 'updatedAt' ocultas temporalmente en la pestaña Forms.
      // Para volver a mostrarlas, descomentar el bloque siguiente (no requiere más cambios:
      // las claves i18n y el mapeo de datos siguen vigentes).
      // {
      //   key: 'createdAt',
      //   title: 'Created at',
      //   titleKey: 'forms.list.table.columns.createdAt',
      //   type: 'text',
      //   width: '120px',
      //   sortable: true,
      //   filterable: false,
      // },
      // {
      //   key: 'updatedAt',
      //   title: 'Updated at',
      //   titleKey: 'forms.list.table.columns.updatedAt',
      //   type: 'text',
      //   width: '120px',
      //   sortable: true,
      //   filterable: false,
      // },
      {
        key: 'partners',
        title: 'Partners',
        titleKey: 'forms.list.table.columns.partners',
        type: 'text',
        width: '100px',
        sortable: true,
        filterable: false,
      },
    ];
    this.cdr.markForCheck();
  }

  private loadForms(): void {
    this.isLoadingForms = true;

    // Obtener countryId y partnerId de la ruta actual
    this.route.paramMap.subscribe((params) => {
      const countryId = params.get('countryId');
      const partnerId = params.get('partnerId');

      // Guardar countryId para uso posterior
      this.currentCountryId = countryId || undefined;

      if (partnerId) {
        // Si estamos en contexto de partner, cargar formularios del partner
        const formParams: FormListParams = {
          offset: this.formsPageIndex * this.formsPageSize,
          limit: this.formsPageSize,
        };

        if (this.formsFilter && this.formsFilter.length >= 3) {
          formParams.filter = this.formsFilter;
        }
        if (this.formsSortBy) {
          formParams.sortBy = this.formsSortBy;
        }
        if (this.formsSortOrder) {
          formParams.sortOrder = this.formsSortOrder;
        }

        this.formService.getAllInPartner(partnerId, formParams).subscribe({
          next: (response) => {
            // Mapear todos los formularios (DRAFT y FINAL)
            this.formsData = response.items.map((form) => {
              const mappedForm = {
                id: form.id,
                name: form.name,
                status: form.status,
                publication: form.publication,
                partnerId: partnerId, // Usar el partnerId del contexto
                published: form.status === 'FINAL' ? '✓' : '',
                createdAt: formatDate(form.createdAt),
                updatedAt: formatDate(form.updatedAt),
                partners: form.publication?.assignments ?? 0,
              };
              return mappedForm;
            });
            this.formsTotalItems = response.total;
            this.isLoadingForms = false;
            this.cdr.markForCheck();
          },
          error: (error) => {
            this.snackbarService.notifyHttpError(error);
            this.formsData = [];
            this.isLoadingForms = false;
            this.cdr.markForCheck();
          },
        });
      } else if (countryId) {
        // Si solo hay countryId, cargar formularios del country
        const formParams: FormListParams = {
          offset: this.formsPageIndex * this.formsPageSize,
          limit: this.formsPageSize,
        };

        if (this.formsFilter && this.formsFilter.length >= 3) {
          formParams.filter = this.formsFilter;
        }
        if (this.formsSortBy) {
          formParams.sortBy = this.formsSortBy;
        }
        if (this.formsSortOrder) {
          formParams.sortOrder = this.formsSortOrder;
        }

        this.formService.getAllInCountry(countryId, formParams).subscribe({
          next: (response) => {
            // Mapear todos los formularios (DRAFT y FINAL)
            this.formsData = response.items.map((form) => {
              const mappedForm = {
                id: form.id,
                name: form.name,
                status: form.status,
                schema: form.schema,
                publication: form.publication,
                countryId: countryId, // Usar el countryId del contexto
                published: form.status === 'FINAL' ? '✓' : '',
                createdAt: formatDate(form.createdAt),
                updatedAt: formatDate(form.updatedAt),
                partners: form.publication?.assignments ?? 0,
              };
              return mappedForm;
            });
            this.formsTotalItems = response.total;
            this.isLoadingForms = false;
            this.cdr.markForCheck();
          },
          error: (error) => {
            this.snackbarService.notifyHttpError(error);
            this.formsData = [];
            this.isLoadingForms = false;
            this.cdr.markForCheck();
          },
        });
      } else {
        this.isLoadingForms = false;
      }
    });
  }

  protected openCreateFormDialog(): void {
    this.openCreateFormDialogInContext();
  }

  private openCreateFormDialogInContext(): void {
    // Obtener countryId de la ruta actual (estamos a nivel de country)
    const countryId = this.route.snapshot.paramMap.get('countryId');

    const dialogRef = this.dialog.open(FormUpsertDialogComponent, {
      width: '90vw',
      maxWidth: '1400px',
      height: '90vh',
      panelClass: ['dialog-fullscreen', 'dialog-safe-area', 'dialog-flex'],
      data: {
        title: this.translateService.instant('forms.form.createTitle'),
        mode: 'create',
        context: 'country',
        countryId: countryId || undefined,
      },
    });

    dialogRef.afterClosed().subscribe((result: FormDialogResult | undefined) => {
      if (result && result.mode === 'create') {
        this.handleFormCreate(result.payload, result.countryId);
      }
    });
  }

  private handlePartnerCreate(payload: any, context?: string, countryId?: string): void {
    // Si estamos en contexto country, usar el endpoint específico
    if (context === 'country' && countryId) {
      // Usar el endpoint: POST /countries/{countryId}/partners
      this.partnerService.createInCountry(countryId, { name: payload.name }).subscribe({
        next: () => {
          this.snackbarService.open(
            this.translateService.instant('partners.messages.created')
          );
          this.loadTable(); // Recargar la tabla de partners
        },
        error: (error) => this.snackbarService.notifyHttpError(error),
      });
    } else {
      // Contexto global - usar el endpoint general
      this.partnerService.create(payload).subscribe({
        next: () => {
          this.snackbarService.open(
            this.translateService.instant('partners.messages.created')
          );
          this.loadTable(); // Recargar la tabla de partners
        },
        error: (error) => this.snackbarService.notifyHttpError(error),
      });
    }
  }

  private handlePartnerUpdate(partnerId: string, payload: any): void {
    this.partnerService.update(partnerId, payload).subscribe({
      next: () => {
        this.snackbarService.open(
          this.translateService.instant('partners.messages.updated')
        );
        this.loadTable(); // Recargar la tabla de partners
      },
      error: (error) => this.snackbarService.notifyHttpError(error),
    });
  }

  private handleFormCreate(payload: any, countryId?: string): void {
    // Crear form (el countryId ya está en el payload si fue proporcionado)
    this.formService.create(payload).subscribe({
      next: () => {
        this.snackbarService.open(
          this.translateService.instant('forms.list.messages.created')
        );
        this.loadForms(); // Recargar la tabla al final
      },
      error: (error) => this.snackbarService.notifyHttpError(error),
    });
  }

  private handleFormUpdate(formId: string, payload: any): void {
    this.formService.update(formId, payload).subscribe({
      next: () => {
        this.snackbarService.open(
          this.translateService.instant('forms.list.messages.updated')
        );
        this.loadForms(); // Recargar la tabla al final
      },
      error: (error) => this.snackbarService.notifyHttpError(error),
    });
  }

  private getCurrentPartnerId(): string | undefined {
    // Obtener el partnerId del contexto actual
    return this.route.snapshot.paramMap.get('partnerId') || undefined;
  }

  protected onFormAction(actionEvent: ActionEvent): void {
    const { event, rowData } = actionEvent;

    switch (event) {
      case 'edit-form':
        this.editForm(rowData);
        break;
      case 'publish-form':
        this.publishFormToPartner(rowData);
        break;
      case 'delete-form':
        this.deleteForm(rowData);
        break;
      case 'copy-link':
        this.copyFormLink(rowData);
        break;
      case 'dashboard-form':
        this.openDashboardForm(rowData);
        break;
      default:
        console.warn('[PartnersList] Form action not recognized:', event);
        break;
    }
  }

  private openDashboardForm(form: any): void {
    // Determinar scope por la ruta actual: partnerId tiene prioridad sobre countryId.
    const partnerId = this.route.snapshot.paramMap.get('partnerId') || undefined;
    const countryId = this.route.snapshot.paramMap.get('countryId') || undefined;

    const responses$ = partnerId
      ? this.formService.getAllSubmittedAnswersForFormInPartner(partnerId, form.id)
      : countryId
        ? this.formService.getAllSubmittedAnswersForFormInCountry(countryId, form.id)
        : this.formService.getAllSubmittedAnswersForForm(form.id);

    const form$ = partnerId
      ? this.formService.getByIdInPartner(partnerId, form.id)
      : countryId
        ? this.formService.getByIdInCountry(countryId, form.id)
        : this.formService.getById(form.id);

    form$.subscribe({
      next: (fullForm) => {
        responses$.subscribe({
          next: (responses) => {
            const scopeKey = partnerId
              ? 'forms.dashboard.scope.partner'
              : countryId
                ? 'forms.dashboard.scope.country'
                : 'forms.dashboard.scope.global';
            // pageTitle ya contiene el nombre dinámico del nivel (country o partner).
            const scopeLabel = this.translateService.instant(scopeKey, {
              name: this.pageTitle || '',
            });

            this.dashboardDialog.open({
              formName: fullForm.name,
              schema: fullForm.schema ?? {},
              responses,
              scopeLabel,
            });
          },
          error: () => {
            this.snackbarService.open(
              this.translateService.instant('forms.dashboard.empty')
            );
          },
        });
      },
      error: (err) => this.snackbarService.notifyHttpError(err),
    });
  }

  /**
   * Abre el formulario en modo lectura al hacer click en una fila
   */
  protected onFormRowClick(form: any): void {
    const dialogData: FormResponseData = {
      title: this.translateService.instant('forms.form.viewTitle'),
      formName: form.name,
      schema: form.schema || {},
      mode: 'view',
      formId: form.id,
    };

    this.dialog.open(FormResponseDialogComponent, {
      data: dialogData,
      panelClass: ['dialog-xl', 'dialog-safe-area', 'dialog-flex'],
      width: '90vw',
      maxWidth: '1200px',
      height: '90vh',
      disableClose: false,
    });
  }

  protected onFormPageChange(event: { pageIndex: number; pageSize: number }): void {
    this.formsPageIndex = event.pageIndex;
    this.formsPageSize = event.pageSize;
    this.loadForms();
  }

  protected onFormSortChange(sort: Sort): void {
    const columnToApiField: Record<string, FormListParams['sortBy']> = {
      name: 'name',
      createdAt: 'createdAt',
      updatedAt: 'updatedAt',
      partners: 'counts.partners',
      beneficiaries: 'publication.submissions.total',
    };

    if (sort.direction) {
      this.formsSortBy = columnToApiField[sort.active];
      this.formsSortOrder = sort.direction.toUpperCase() as 'ASC' | 'DESC';
    } else {
      this.formsSortBy = undefined;
      this.formsSortOrder = undefined;
    }

    this.formsPageIndex = 0;
    this.loadForms();
  }

  protected onFormsGlobalSearch(searchTerm: string): void {
    this.formsFilter = searchTerm;
    if (searchTerm && searchTerm.length >= 3) {
      this.formsPageIndex = 0;
      this.loadForms();
    } else if (!searchTerm || searchTerm.length === 0) {
      this.formsFilter = undefined;
      this.loadForms();
    }
  }

  protected onFormFilterChange(filters: Record<string, string>): void {
    const filterValue = Object.values(filters).find((v) => v && v.length >= 3);
    if (filterValue) {
      this.formsFilter = filterValue;
      this.formsPageIndex = 0;
      this.loadForms();
    } else if (Object.values(filters).every((v) => !v || v.length === 0)) {
      this.formsFilter = undefined;
      this.loadForms();
    }
  }

  private publishFormToPartner(form: any): void {
    const countryId = this.currentCountryId;

    if (!countryId) {
      this.snackbarService.error('common.errors.noCountryContext');
      return;
    }

    if (form.status === 'DRAFT') {
      // DRAFT → finalize primero, luego publish
      this.formService.finalize(form.id).subscribe({
        next: () => {
          this.formService.publishToCountry(countryId, form.id).subscribe({
            next: () => {
              this.snackbarService.open(
                this.translateService.instant('forms.list.messages.finalizedAndPublished')
              );
              this.loadForms();
            },
            error: (err) => {
              this.snackbarService.notifyHttpError(err);
              this.loadForms(); // Recargar igualmente porque finalize sí fue exitoso
            },
          });
        },
        error: (err) => this.snackbarService.notifyHttpError(err),
      });
    } else {
      // FINAL → solo publish
      this.formService.publishToCountry(countryId, form.id).subscribe({
        next: () => {
          this.snackbarService.open(
            this.translateService.instant('forms.list.messages.publishedToCountry')
          );
          this.loadForms();
        },
        error: (err) => this.snackbarService.notifyHttpError(err),
      });
    }
  }

  private getCurrentCountryId(): string | undefined {
    // Retornar el countryId guardado
    return this.currentCountryId;
  }

  private editForm(form: any): void {
    const dialogRef = this.dialog.open(FormUpsertDialogComponent, {
      width: '90vw',
      maxWidth: '1400px',
      height: '90vh',
      panelClass: ['dialog-fullscreen', 'dialog-safe-area', 'dialog-flex'],
      data: {
        title: this.translateService.instant('forms.form.editTitle'),
        mode: 'edit',
        form: form,
        context: 'partner',
        partnerId: this.getCurrentPartnerId(),
      },
    });

    dialogRef.afterClosed().subscribe((result: FormDialogResult | undefined) => {
      if (result && result.mode === 'edit' && result.formId) {
        this.handleFormUpdate(result.formId, result.payload);
      }
    });
  }

  private copyFormLink(form: any): void {
    const baseUrl = window.location.origin;
    const currentPath = window.location.pathname;

    const identifier = form.publication?.token || form.id;

    const isGitHubPages = window.location.hostname.includes('github.io');
    const isGitHubPagesDev = isGitHubPages && currentPath.startsWith('/jrs-forms-web/');
    const basePath = isGitHubPagesDev ? '/jrs-forms-web' : '';
    const url = `${baseUrl}${basePath}/#/public/form/${identifier}`;

    navigator.clipboard.writeText(url).then(() => {
      this.snackbarService.open(
        this.translateService.instant('forms.list.messages.linkCopied')
      );
    });
  }

  private deleteForm(form: any): void {
    const dialogRef = this.dialog.open(UiMoleculeDialogDeleteWithConfirmComponent, {
      disableClose: true,
      autoFocus: false,
      panelClass: ['dialog-sm', 'dialog-safe-area'],
      data: { itemName: form.name },
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (!confirmed) return;

      this.formService.delete(form.id).subscribe({
        next: () => {
          this.loadForms(); // Recargar la lista de forms
        },
        error: (err) => this.snackbarService.notifyHttpError(err),
      });
    });
  }

  private handleUserUpdate(userId: string, userData: UserUpdate, newPassword?: string): void {
    // Primero actualizar los datos del usuario
    this.userService.update(userId, userData).subscribe({
      next: (updatedUser) => {
        // Si se proporcionó una nueva contraseña, cambiarla por separado
        if (newPassword && newPassword.trim() !== '') {
          this.userService.changePassword(userId, { newPassword }).subscribe({
            next: (userWithNewPassword) => {
              this.loadUsers(); // Recargar tabla al final
            },
            error: (error) => {
              // Aunque falle el cambio de contraseña, los datos del usuario ya se actualizaron
              this.snackbarService.notifyHttpError(error);
              this.loadUsers();
            },
          });
        } else {
          // No se cambió la contraseña, solo recargar la tabla
          this.loadUsers();
        }
      },
      error: (error) => this.snackbarService.notifyHttpError(error),
    });
  }

  protected onExportAllPartners(event: { selectedKeys: string[] }): void {
    this.route.paramMap.subscribe((params) => {
      const countryId = params.get('countryId');

      this.partnerService.getAllForExport({ countryId: countryId || undefined }).subscribe({
        next: (allPartners) => {
          const exportData = allPartners.map((partner) => ({
            id: partner.id,
            name: partner.name,
            beneficiariesCount: partner.counts?.beneficiaries || 0,
            createdAt: formatDate(partner.createdAt),
            updatedAt: formatDate(partner.updatedAt),
          }));

          const partnersTable = this.tables.toArray()[0];
          if (partnersTable) {
            partnersTable.exportFilteredDataToExcel(exportData, event.selectedKeys);
          }
        },
        error: (err) => this.snackbarService.notifyHttpError(err),
      });
    });
  }

  /**
   * Exportar todos los usuarios (no solo los paginados)
   * Partners List está en /countries/{countryId}/partners - usa countryId para filtrar usuarios
   */
  protected onExportAllUsers(): void {
    this.route.paramMap.subscribe((params) => {
      const countryId = params.get('countryId');

      if (!countryId) {
        this.snackbarService.error('common.errors.missingCountryId');
        return;
      }

      this.userService
        .getAllForExport({
          countryId: countryId,
        })
        .subscribe({
          next: (allUsers) => {
            // Mapear al formato de la tabla
            const exportData = allUsers.map((user) => ({
              id: user.id,
              email: user.email,
              firstName: user.firstName,
              lastName: user.lastName,
              enabled: user.status === 'ACTIVE' ? '✓' : '',
              status: user.status,
              createdAt: formatDate(user.createdAt),
              updatedAt: formatDate(user.updatedAt),
            }));

            // Exportar usando la tabla de usuarios (tercera tabla)
            const usersTable = this.tables.toArray()[2];
            if (usersTable) {
              usersTable.exportAllDataToExcel(exportData);
            }
          },
          error: (err) => this.snackbarService.notifyHttpError(err),
        });
    });
  }

  protected onExportAllForms(event: { selectedKeys: string[] }): void {
    this.route.paramMap.subscribe((params) => {
      const countryId = params.get('countryId');

      if (!countryId) {
        this.snackbarService.error('common.errors.missingCountryId');
        return;
      }

      this.formService.getAllForExportInCountry(countryId, {}).subscribe({
        next: (allForms) => {
          const exportData = allForms.map((form) => ({
            id: form.id,
            name: form.name,
            published: form.status === 'FINAL' ? '✓' : '',
            createdAt: formatDate(form.createdAt),
            updatedAt: formatDate(form.updatedAt),
            partners: (form as any).publication?.assignments ?? 0,
          }));

          const formsTable = this.tables.toArray()[1];
          if (formsTable) {
            formsTable.exportFilteredDataToExcel(exportData, event.selectedKeys);
          }
        },
        error: (err) => this.snackbarService.notifyHttpError(err),
      });
    });
  }
}
